// src/components/PrayerTimesCompact/PrayerTimesCompact.tsx
import React, { useState, useEffect } from 'react';
import './PrayerTimesCompact.css';

interface PrayerTime {
  name: string;
  nameFr: string;
  nameAr: string;
  time: string;
  isPrayer: boolean; // Pour distinguer les prières du Chourouk
}

interface CityResult {
  display_name: string;
  lat: string;
  lon: string;
  name: string;
  address: {
    city?: string;
    town?: string;
    village?: string;
    country?: string;
  };
}

interface CalculationMethod {
  id: number;
  name: string;
  description: string;
  icon: string;
}

const CALCULATION_METHODS: CalculationMethod[] = [
  { 
    id: 2, 
    name: 'ISNA', 
    description: 'Islamic Society of North America (15° / 15°)',
    icon: '📚'
  },
  { 
    id: 4, 
    name: 'Umm al-Qura', 
    description: 'Arabie Saoudite (18.5° / 90 min après Maghrib)',
    icon: '🕋'
  }
];

// Fonction pour obtenir le texte complet de la méthode
const getMethodFullText = (methodId: number): string => {
  switch(methodId) {
    case 2:
      return 'Méthode ISNA • 15° Fajr / 15° Isha';
    case 4:
      return 'Méthode Umm al-Qura • 18.5° Fajr / 90 min Isha';
    default:
      return 'Méthode ISNA • 15° Fajr / 15° Isha';
  }
};

// Fonction pour obtenir l'icône de la méthode
const getMethodIcon = (methodId: number): string => {
  switch(methodId) {
    case 2:
      return '📚';
    case 4:
      return '🕋';
    default:
      return '📚';
  }
};

const PrayerTimesCompact: React.FC = () => {
  const [prayers, setPrayers] = useState<PrayerTime[]>([]);
  const [city, setCity] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showCitySelector, setShowCitySelector] = useState(false);
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<CityResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<number>(2);

  useEffect(() => {
    initializePrayerTimes();
  }, []);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchCities(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const initializePrayerTimes = async () => {
    // Charger la méthode sauvegardée
    const savedMethod = localStorage.getItem('calculationMethod');
    if (savedMethod) {
      setSelectedMethod(parseInt(savedMethod));
    }

    const savedCity = localStorage.getItem('selectedCity');
    
    if (savedCity) {
      const cityData = JSON.parse(savedCity);
      setCity(cityData.city);
      setCountry(cityData.country);
      await getPrayerTimesFromAPI(cityData.lat, cityData.lng, savedMethod ? parseInt(savedMethod) : 2);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          await getPrayerTimesFromAPI(latitude, longitude, savedMethod ? parseInt(savedMethod) : 2);
          await getCityFromCoordinates(latitude, longitude);
        },
        () => {
          setShowCitySelector(true);
          setLoading(false);
        }
      );
    } else {
      setShowCitySelector(true);
      setLoading(false);
    }
  };

  const handleMethodChange = async (methodId: number) => {
    setSelectedMethod(methodId);
    localStorage.setItem('calculationMethod', methodId.toString());
    
    // Recharger les horaires avec la nouvelle méthode
    const savedCity = localStorage.getItem('selectedCity');
    if (savedCity) {
      const cityData = JSON.parse(savedCity);
      await getPrayerTimesFromAPI(cityData.lat, cityData.lng, methodId);
    }
    
    setShowMethodSelector(false);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Votre navigateur ne supporte pas la géolocalisation');
      return;
    }

    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        await getCityFromCoordinates(latitude, longitude);
        await getPrayerTimesFromAPI(latitude, longitude, selectedMethod);
        
        const cityData = {
          city: city || 'Votre ville',
          country: country || 'Votre pays',
          lat: latitude,
          lng: longitude
        };
        localStorage.setItem('selectedCity', JSON.stringify(cityData));
        
        setGeolocating(false);
      },
      (error) => {
        setGeolocating(false);
        alert('Impossible d\'obtenir votre position. Veuillez autoriser la géolocalisation.');
      }
    );
  };

  const searchCities = async (query: string) => {
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query)}&` +
        `format=json&` +
        `addressdetails=1&` +
        `limit=30&` +
        `accept-language=fr`
      );
      
      const data = await response.json();
      
      const cities = data.filter((item: CityResult) => 
        item.address?.city || item.address?.town || item.address?.village
      );
      
      const uniqueCities = cities.reduce((acc: CityResult[], current: CityResult) => {
        const cityName = current.address.city || current.address.town || current.address.village || current.name;
        const countryName = current.address.country || '';
        const key = `${cityName}-${countryName}`;
        
        const exists = acc.some((item) => {
          const existingCity = item.address.city || item.address.town || item.address.village || item.name;
          const existingCountry = item.address.country || '';
          return `${existingCity}-${existingCountry}` === key;
        });
        
        if (!exists) {
          acc.push(current);
        }
        
        return acc;
      }, []);
      
      setSearchResults(uniqueCities);
    } catch (err) {
      console.error('Erreur recherche villes:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleCitySelect = async (cityResult: CityResult) => {
    const cityName = cityResult.address.city || 
                     cityResult.address.town || 
                     cityResult.address.village || 
                     cityResult.name;
    const countryName = cityResult.address.country || '';
    
    const cleanCity = cityName.replace(/\s*\([^)]*\)/g, '').trim();
    const cleanCountry = countryName.replace(/\s*\([^)]*\)/g, '').trim();
    
    localStorage.setItem('selectedCity', JSON.stringify({
      city: cleanCity,
      country: cleanCountry,
      lat: parseFloat(cityResult.lat),
      lng: parseFloat(cityResult.lon)
    }));
    
    setCity(cleanCity);
    setCountry(cleanCountry);
    await getPrayerTimesFromAPI(parseFloat(cityResult.lat), parseFloat(cityResult.lon), selectedMethod);
    setShowCitySelector(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleChangeCity = () => {
    setShowCitySelector(true);
  };

  const handleToggleMethodSelector = () => {
    setShowMethodSelector(!showMethodSelector);
  };

  const getCityFromCoordinates = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?` +
        `lat=${lat}&` +
        `lon=${lng}&` +
        `format=json&` +
        `addressdetails=1&` +
        `accept-language=fr`
      );
      
      const data = await response.json();
      
      if (data && data.address) {
        const cityName = data.address.city || 
                        data.address.town || 
                        data.address.village || 
                        data.address.county || 
                        'Votre ville';
        const countryName = data.address.country || 'Votre pays';
        
        const cleanCity = cityName.replace(/\s*\([^)]*\)/g, '').trim();
        const cleanCountry = countryName.replace(/\s*\([^)]*\)/g, '').trim();
        
        setCity(cleanCity);
        setCountry(cleanCountry);
      }
    } catch (err) {
      console.error('Erreur géolocalisation:', err);
      setCity('Votre ville');
      setCountry('Votre pays');
    }
  };

  const getPrayerTimesFromAPI = async (lat: number, lng: number, method: number = 2) => {
    try {
      const response = await fetch(
        `https://api.aladhan.com/v1/timings?latitude=${lat}&longitude=${lng}&method=${method}`
      );
      
      const data = await response.json();
      
      if (data.code === 200) {
        const timings = data.data.timings;
        
        const prayerList: PrayerTime[] = [
          { name: 'Fajr', nameFr: 'Fajr', nameAr: 'الفجر', time: timings.Fajr, isPrayer: true },
          { name: 'Sunrise', nameFr: 'Chourouk', nameAr: 'الشروق', time: timings.Sunrise, isPrayer: false },
          { name: 'Dhuhr', nameFr: 'Dhuhr', nameAr: 'الظهر', time: timings.Dhuhr, isPrayer: true },
          { name: 'Asr', nameFr: 'Asr', nameAr: 'العصر', time: timings.Asr, isPrayer: true },
          { name: 'Maghrib', nameFr: 'Maghrib', nameAr: 'المغرب', time: timings.Maghrib, isPrayer: true },
          { name: 'Isha', nameFr: 'Isha', nameAr: 'العشاء', time: timings.Isha, isPrayer: true }
        ];
        
        setPrayers(prayerList);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (showCitySelector) {
    return (
      <div className="prayer-times-compact">
        <div className="city-selector">
          <div className="city-selector-header">
            <h3>🕌 Choisissez votre ville</h3>
            <p>Recherchez n'importe quelle ville dans le monde</p>
          </div>
          
          <div className="search-box">
            <input
              type="text"
              className="city-search-input"
              placeholder="🔍 Tapez votre ville (ex: Paris, Dubai, Londres...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searching && <div className="search-loader">🔄</div>}
          </div>
          
          <div className="city-list">
            {searchTerm.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="search-hint">
                ❌ Aucune ville trouvée. Essayez une autre orthographe.
              </div>
            )}
            
            {searchResults.map((result, index) => {
              const cityName = result.address.city || result.address.town || result.address.village || result.name;
              const countryName = result.address.country || '';
              
              return (
                <button
                  key={index}
                  className="city-item"
                  onClick={() => handleCitySelect(result)}
                >
                  <span className="city-name">{cityName}</span>
                  <span className="city-country">{countryName}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prayer-times-compact">
      {/* Header avec boutons */}
      <div className="prayer-compact-header">
        <div className="prayer-location-actions">
          <button 
            className="use-location-btn"
            onClick={handleUseMyLocation}
            disabled={geolocating}
            title="Utiliser ma position GPS"
          >
            {geolocating ? '🔄' : '📍'} Ma position
          </button>
          
          <div className="location-display">
            <span className="location-text">
              {city && country ? `${city}, ${country}` : 'Votre position'}
            </span>
          </div>
          
          <button 
            className="choose-city-btn"
            onClick={handleChangeCity}
            title="Choisir une autre ville"
          >
            Choisir ma ville
          </button>
        </div>
      </div>

      {/* Corps avec les 5 prières + Chourouk */}
      <div className="prayer-times-compact-body">
        {prayers.map((prayer, index) => (
          <div 
            key={index} 
            className={`prayer-compact-item ${!prayer.isPrayer ? 'chourouk-item' : ''}`}
          >
            <span className={`prayer-compact-name-ar ${!prayer.isPrayer ? 'chourouk-name-ar' : ''}`}>
              {prayer.nameAr}
            </span>
            <span className={`prayer-compact-name-fr ${!prayer.isPrayer ? 'chourouk-name-fr' : ''}`}>
              {prayer.nameFr}
            </span>
            <span className={`prayer-compact-time ${!prayer.isPrayer ? 'chourouk-time' : ''}`}>
              {prayer.time}
            </span>
          </div>
        ))}
      </div>

      {/* Footer avec méthode CLIQUABLE */}
      <div className="prayer-compact-footer">
        <button 
          className="method-selector-btn"
          onClick={handleToggleMethodSelector}
          title="Changer la méthode de calcul"
        >
          <span className="prayer-method-icon">{getMethodIcon(selectedMethod)}</span>
          <span className="prayer-method-text">
            {getMethodFullText(selectedMethod)}
          </span>
          <span className="method-arrow">{showMethodSelector ? '▲' : '▼'}</span>
        </button>

        {/* Sélecteur déroulant */}
        {showMethodSelector && (
          <div className="method-dropdown">
            {CALCULATION_METHODS.map(method => (
              <button
                key={method.id}
                className={`method-option ${method.id === selectedMethod ? 'active' : ''}`}
                onClick={() => handleMethodChange(method.id)}
              >
                <span className="method-icon">{method.icon}</span>
                <div className="method-info">
                  <span className="method-name">{method.name}</span>
                  <span className="method-desc">{method.description}</span>
                </div>
                {method.id === selectedMethod && <span className="method-check">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrayerTimesCompact;